import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { FIELD_TYPES } from '../../../../../shared/fields/field-types';
import { Disposition, ExcludedDisposition } from '../../../core/store/models';
import { rootSelectors } from '../../../core/store/selectors';
import * as dispositionActions from '../../../core/store/dispositions/disposition.actions';
import * as excludedDispositionActions from '../../../core/store/excluded-dispositions/excluded-disposition.actions';
import { ActionStatus } from '../../../core/store/types';

export enum DispositionModalTypes {
    Create,
    Edit
};
export interface DispositionModalData {
    studioId: number;
    disposition?: Disposition;
    excluded?: boolean;
}
@Component({
    selector: 'ah-disposition-modal',
    templateUrl: './disposition-modal.component.html',
    styleUrls: ['./disposition-modal.component.scss'],
    standalone: false
})
export class DispositionModalComponent implements OnInit {
    form: UntypedFormGroup;
    DispositionModalTypes = DispositionModalTypes;
    modalType: DispositionModalTypes;
    nameField = {
        name: 'Name',
        canonical_name: 'name',
        type: FIELD_TYPES.SHORT_TEXT_AREA.type,
    };
    codeField = {
        name: 'Code',
        canonical_name: 'code',
        type: FIELD_TYPES.NUMBER.type,
    };
    colorField = {
        name: 'Color',
        canonical_name: 'color',
        type: FIELD_TYPES.SHORT_TEXT_AREA.type,
    };
    saving = false;
    disposition: Disposition;
    constructor(@Inject(MAT_DIALOG_DATA) private data: DispositionModalData, private fb: UntypedFormBuilder, private store: Store<rootSelectors.State>, public dialogRef: MatDialogRef<DispositionModalComponent>) { }

    ngOnInit(): void {
        this.modalType = this.data.disposition ? DispositionModalTypes.Edit : DispositionModalTypes.Create;
        this.disposition = this.data.disposition;
        this.buildForm();
    }

    handleNameInput() {
        const nameControl: AbstractControl = this.form.get('name');
        let nameValue: string;

        if (nameControl) {
            nameValue = nameControl.value;
        }

        if (this.modalType === DispositionModalTypes.Create) {
            // Replace all spaces with underscores, remove all special characters besides underscore, lowercase
            this.form.patchValue({ canonical_name: nameValue.replace(/ /g, '_').replace(/\W+/g, '').toLowerCase() });
        }
    }

    buildForm() {
        const formObj: { [formControlName: string]: any } = {
            [this.nameField.canonical_name]: [this.disposition ? this.disposition.name : '', [Validators.required, Validators.maxLength(100)]],
            [this.codeField.canonical_name]: [this.disposition ? this.disposition.code : '', Validators.required],
            [this.colorField.canonical_name]: [this.disposition ? this.disposition.color : '', [Validators.required, Validators.maxLength(7), Validators.minLength(7)]],
        };

        this.form = this.fb.group(formObj);
    }

    onSave() {
        const values = this.form.getRawValue();

        if (!this.data.excluded) {
            const newDisposition: Disposition = new Disposition({
                ...values,
                studio_id: this.data.studioId,
            });

            this.saving = true;
            if (this.modalType === DispositionModalTypes.Create) {
                this.store.dispatch(new dispositionActions.AddAction({
                    ...newDisposition
                }));
            } else {
                this.store.dispatch(new dispositionActions.UpdateAction({
                    ...newDisposition,
                    id: this.disposition.id
                }));
            }

            this.store.select(rootSelectors.getDispositionSaving).pipe(filter(s => s !== ActionStatus.Loading), take(1)).subscribe(status => {
                this.saving = false;

                if (status === ActionStatus.Complete) {
                    this.dialogRef.close(true);
                } else {
                    this.dialogRef.close(false);
                }
            });
        } else if (this.data.excluded) {
            const newExcludedDisposition: ExcludedDisposition = new ExcludedDisposition({
                ...values,
                studio_id: this.data.studioId,
            });

            this.saving = true;
            if (this.modalType === DispositionModalTypes.Create) {
                this.store.dispatch(new excludedDispositionActions.AddAction({
                    ...newExcludedDisposition
                }));
            } else {
                this.store.dispatch(new excludedDispositionActions.UpdateAction({
                    ...newExcludedDisposition,
                    id: this.disposition.id
                }));
            }

            this.store.select(rootSelectors.getExcludedDispositionSaving).pipe(filter(s => s !== ActionStatus.Loading), take(1)).subscribe(status => {
                this.saving = false;

                if (status === ActionStatus.Complete) {
                    this.dialogRef.close(true);
                } else {
                    this.dialogRef.close(false);
                }
            });
        }
    }
}
